import {
  a,
  detailsBigCss,
  h2Css,
  h6Css,
} from "@/styles/typography";
import styled from "styled-components";

const Header = styled.div<{ isOpen: boolean; scrolled: number }>`
  width: 100%;
  height: 80px;
  padding: 13px 0px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  background-color: ${({ scrolled, isOpen }) =>
    scrolled || isOpen ? "#124d60" : "transparent"};
  color: ${({ scrolled, isOpen }) =>
    scrolled || isOpen ? "red" : "yellow"};
  box-shadow: ${({ scrolled, isOpen }) =>
    scrolled || isOpen ? "0px 5px 20px rgba(0, 0, 0, 0.1)" : "none"};
  transition: 0.3s ease-in-out;
`;

const Wrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  height: 100%;
`;

const Div = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 50px;

  @media ${({ theme }) => theme.breakpoints.sm} {
    gap: 20px;
  }
`;

const Hamburger = styled.div<{ isOpen: boolean }>`
  z-index: 100;
  cursor: pointer;
  margin-top: 6px;
  transition: color 0.1s ease-in-out;
  transform: ${({ isOpen }) =>
    isOpen ? "translateX(10px) " : "translateX(0)"};

  @media screen and (min-width: 821px) {
    display: none;

    &:hover hr {
      color: ${({ theme }) => theme.colors.tertiary};
    }
  }
`;

const HamburgerLine1 = styled.div<{ isOpen: boolean; scrolled: number }>`
  width: 36px;
  height: 1px;
  color: ${({ theme, scrolled, isOpen }) =>
    scrolled || isOpen ? theme.textColors.primary : theme.textColors.secondary};
  margin-bottom: 9px;
  border-top: 2px solid;
  border-color: ${({ theme, scrolled, isOpen }) =>
    scrolled || isOpen ? theme.textColors.secondary : theme.textColors.secondary};
  transform: ${({ isOpen }) =>
    isOpen ? "rotate(-45deg) translateX(-15px) " : "rotate(0) translateX(0)"};
  transition: transform 0.3s ease-in-out;
`;
const HamburgerLine2 = styled.div<{ isOpen: boolean; scrolled: number }>`
  width: 36px;
  height: 1px;
  color: ${({ theme, scrolled, isOpen }) =>
    scrolled || isOpen ? theme.textColors.primary : theme.textColors.secondary};
  margin-bottom: 9px;
  border-top: 2px solid;
  border-color: ${({ theme, scrolled, isOpen }) =>
    scrolled || isOpen ? theme.textColors.secondary : theme.textColors.secondary};
  transform: ${({ isOpen }) => (isOpen ? "translateX(60px)" : "translateX(0)")};
  opacity: ${({ isOpen }) => (isOpen ? "0" : "1")};
  transition: transform 0.3s ease-in-out;
`;
const HamburgerLine3 = styled.div<{ isOpen: boolean; scrolled: number }>`
  width: 36px;
  height: 1px;
  color: ${({ theme, scrolled, isOpen }) =>
    scrolled || isOpen ? theme.textColors.primary : theme.textColors.secondary};
  margin-bottom: 9px;
  border-top: 2px solid;
  border-color: ${({ theme, scrolled, isOpen }) =>
    scrolled || isOpen ? theme.textColors.secondary : theme.textColors.secondary};
  transform: ${({ isOpen }) =>
    isOpen ? "rotate(45deg) translateX(-15px)" : "rotate(0) translateX(0)"};
  transition: transform 0.3s ease-in-out;
`;

const Container = styled.div<{ isOpen: boolean }>`
  width: 100%;
  height: 100vh;
  min-height: fit-content;
  position: fixed;
  left: 0;
  top: ${({ isOpen }) => (isOpen ? "80px" : "-100vh")};

  opacity: ${({ isOpen }) => (isOpen ? "1" : "0")};
  visibility: ${({ isOpen }) => (isOpen ? "visible" : "hidden")};
  transition: top 0.3s linear, opacity 3s linear, visibility 0.3s linear;
  padding: 100px 0;
  background: ${({ theme }) => theme.colors.primary};
  transition: all 0.5s ease-in-out;
  z-index: 1;
  overflow-y: ${({ isOpen }) => (isOpen ? "hidden" : "scroll")};

  .active {
    background-color: rgb(20 48 59);
  }

  @media ${({ theme }) => theme.breakpoints.md} {
    padding: 20px 0;
  }

  @media screen and (max-height: 750px) {
    padding: 20px 0;
  }
`;

const LogoLink = styled.a<{ scrolled: number; isOpen: boolean }>`
  text-decoration: none;
  height: 100%;
  width: 100%;

  .gatsby-image-logo {
    height: auto;
    width: 120px;
    transition: all 0.1s ease-in-out;
    filter: ${({ scrolled, isOpen }) =>
      scrolled || isOpen
        ? "invert(94%) sepia(6%) saturate(23%) hue-rotate(204deg) brightness(106%) contrast(105%)"
        : "invert(94%) sepia(6%) saturate(23%) hue-rotate(204deg) brightness(106%) contrast(105%)"};
  }
`;

const Logo = styled.img<{ scrolled: number; isOpen: boolean }>`
  height: 32px;
  transition: all 0.1s ease-in-out;
  filter: ${({ scrolled, isOpen }) =>
    scrolled || isOpen
      ? ""
      : "invert(94%) sepia(6%) saturate(23%) hue-rotate(204deg) brightness(106%) contrast(105%)"};
`;

const Icon = styled.img<{ scrolled: number; isOpen: boolean }>`
  height: 32px;
  cursor: pointer;
  transition: all 0.1s ease-in-out;
  filter: ${({ scrolled, isOpen }) =>
    scrolled || isOpen
      ? "invert(94%) sepia(6%) saturate(23%) hue-rotate(204deg) brightness(106%) contrast(105%)"
      : ""};
  @media screen and (min-width: 821px) {
    &:hover {
      filter: ${({ theme }) => theme.filter.gold};
    }
  }
`;

const Link = styled.a<{ scrolled: number; isActive?: boolean }>`
  ${h2Css}
  text-decoration: none;
  text-transform: uppercase;
  display: flex;
  color: ${({ theme }) => theme.textColors.secondary};
  padding: 20px 0;
  transition: all 0.1s ease-in-out;

  @media screen and (min-width: 821px) {
    &:hover {
      color: ${({ theme }) => theme.textColors.tertiary};
    }
  }

  @media ${({ theme }) => theme.breakpoints.md} {
    margin-bottom: 0;
  }

  @media screen and (max-height: 750px) {
    margin-bottom: 10px;
    font-size: 30px;
  }
`;

const LinkContainer = styled.div`
  padding: 0 70px;
  display: flex;

  :last-of-type {
    padding-bottom: 150px;
  }

  @media ${({ theme }) => theme.breakpoints.sm} {
    padding: 0 20px;
  }
`;

const VerticalLine = styled.hr<{ isOpen: boolean; scrolled: number }>`
  width: 2px;
  border-top: 1px;
  border-style: solid;
  height: 54px;
  border-color: ${({ theme, scrolled, isOpen }) =>
    scrolled || isOpen ? "#888888" : "#999"};
  transform: translateX(0);
  opacity: 1;
  transition: transform 0.3s ease-in-out;

  @media ${({ theme }) => theme.breakpoints.md} {
    display: none;
  }
`;

const Login = styled(a)<{ isOpen: boolean; scrolled: number }>`
  ${h6Css}
  color: ${({ theme, scrolled, isOpen }) =>
    scrolled || isOpen ? theme.textColors.secondary : theme.textColors.secondary};
  text-transform: uppercase;
  text-decoration: none;
  white-space: nowrap;
  transition: all 0.1s ease-in-out;

  @media screen and (min-width: 821px) {
    &:hover {
      color: ${({ theme }) => theme.textColors.tertiary};
    }
  }

  @media ${({ theme }) => theme.breakpoints.md} {
    display: none;
  }
`;

const MobileLogin = styled(a)`
  display: none;

  @media ${({ theme }) => theme.breakpoints.md} {
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 20px 48px 16px 48px;
    border: 2px solid #f9f9f9;
    margin: 10px auto;
    ${detailsBigCss}
    color:white;
    text-transform: uppercase;
    text-decoration: none;
    width: 100%;
  }
  &:hover {
      background-color: ${({ theme }) => theme.backgroundColors.primary};
      color: ${({ theme }) => theme.textColors.primary};
  }
`;

const MainInner = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
  width: 100vw;
  height: 100%;
  max-width: 1440px;
  padding: 0 100px;
  margin: 0 auto;

  @media screen and (max-width: 992px) {
    padding: 0 40px;
  }

  @media screen and (max-width: 600px) {
    max-width: 100%;
    min-width: 100vw;
    min-height: 100vh;
    padding: 20px 20px;
  }
`;

const Dropdown = styled.div<{ index?: number }>`
  position: fixed;
  top: 4rem;
  display: flex;
  flex-direction: column;
  background: #124d60;
  left: 0;
  right: 0;
  border-radius: 5px;
  padding-top: 0;
  z-index: 10;
  height: 70vh;
  max-width: 700px;
  margin: 0 auto;
  margin-left: 25%;
  overflow: hidden;

  &:hover {
    display: flex;
  }

  @media screen and (max-width: 600px) {
    display: none;
  }
`;

const DropdownLink = styled.a`
  color: white;
  font-family: Avenir next bold;
  font-weight: bold;
  font-size: 14px;
  text-decoration: none;
  padding: 1rem !important;
  cursor: pointer;
  vertical-align: middle;

  &:hover {
    color: #a78f67;
  }
`

const PressLink = styled(DropdownLink)`
  display: flex;
  justify-content: space-between;
  margin-left: 0;
  font-size: 12px;
  padding: 0.8rem;
  font-weight: 400;
  border-bottom: 0.5px solid grey;

  p {
    margin-right: 0.5rem;
  }

  span {
    float: right;
  }

  &:last-of-type {
    border-bottom: none;
  }
`;

const PressContainer = styled.div`
  overflow-y: scroll;
  
  ::-webkit-scrollbar {
    display: none;
  }

  scrollbar-width: none;  /* Firefox */
`;

const LatestPress = styled.div`
  background-color: #1d3e48;
  position: relative;
  top: 0rem;
  font-size: 0.8rem;
  display: flex;
  flex-direction: column;
  left: 0rem;
  height: 70vh;
  width: 18rem;
  border-radius: 5px 0 0 5px;
  padding: 1rem;
  padding-top: 0;
  z-index: 10;

  @media screen and (max-width: 600px) {
    display: none;
  }
`;

const Description = styled.p`
  color: #fff;
  font-family: Avenir Next bold;
  font-size: 14px;
  color: #a0a0a0;
  font-style: italic;
  padding: 0.2rem;
`;

const PressTitle = styled.p`
  color: #fff;
  margin: 2rem 0.8rem 0.8rem 0.8rem;
  padding-left: 0.5rem;
  font-family: Avenir next bold;
  text-transform: uppercase;
  font-size: 16px;
  font-weight: bold;
  color: #ceb181;
  border-left: 5px solid;

  &:hover {
    cursor: pointer;
    color: #fff;
  }
`;

const CloseMenu = styled.button`
  background: none;
  border: none;
  position: absolute;
  top: 0.5rem;
  right: 1rem;
  font-weight: bold;
  font-size: 1rem;
  color: #fff;

   &:hover {
      pointer: cursor;
      font-size: 1.2rem;
      color: ${({ theme }) => theme.colors.tertiary};
    }
`;

const HeaderLink = styled(Link)`
  ${h6Css}
  font-size: 16px;
  font-weight: bold;
  text-decoration: none;
  padding: 0.8rem;
  font-family: Avenir next bold;
  padding-bottom: 2rem;
  color: #ceb181;
  white-space: nowrap;
  &:hover {
    
  }
`;

const TitleHeader = styled(HeaderLink)`
  border-left: 5px solid;
  padding-bottom: 0.1rem;
  padding-top: 0.2rem;
  padding-left: 0.5rem;
`;

const MenuLink = styled(HeaderLink)<{ isOpen: boolean; scrolled: number }>`
  padding: 1rem;
  font-size: 14px;
  color: #ffffff;
  @media (max-width: 1100px) {
    font-size: 1rem;
    padding: 0;
    padding-right: 0;
  }

  @media (max-width: 821px) {
    display: none;
  }
`;

const MenuContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 2rem;
  padding-left: 5rem;
  height: 70vh;
  max-height: 800px;
  position: absolute;
  left: 40%;
`;

const HeaderLinkContainer = styled(Div)`
  align-items: flex-start;
  gap: 1rem;
  justify-content: flex-start;
  flex-wrap: nowrap;
  width: 100%;
  justify-content: space-evenly;
  margin-top: 1rem;
  overflow-x: scroll;
  margin-left: 1rem;
  margin-right: 1rem;

  ::-webkit-scrollbar {
    display: none;
  }

  scrollbar-width: none;  /* Firefox */
`;

const ContainerDiv = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-evenly;
`;

const S = {
  Header,
  Container,
  Logo,
  LogoLink,
  Link,
  LinkContainer,
  Hamburger,
  HamburgerLine1,
  HamburgerLine2,
  HamburgerLine3,
  MainInner,
  Wrapper,
  Icon,
  Div,
  VerticalLine,
  Login,
  MobileLogin,
  HeaderLink,
  TitleHeader,
  Dropdown,
  HeaderLinkContainer,
  LatestPress,
  CloseMenu,
  MenuContainer,
  PressTitle,
  DropdownLink,
  MenuLink,
  PressLink,
  PressContainer,
  Description,
  ContainerDiv
};

export default S;
